/**
 * CSS Scoping Utility
 *
 * Prefixes CSS selectors with a scope selector to prevent style conflicts
 * between multiple instances of the same component on a page.
 *
 * @example
 * ```ts
 * const css = `.ai-sections-container { color: red; }`;
 * const scoped = scopeCSS(css, '.ai-sections-abc123');
 * // Result: `.ai-sections-abc123 .ai-sections-container { color: red; }`
 * ```
 *
 * @limitations
 * - Nested @media/@supports blocks (e.g., @media { @supports { ... } }) are not
 *   scoped correctly. The CSS validator will warn if nested at-rules are detected.
 * - CSS strings containing unescaped braces (e.g., `content: "{"`) may break parsing.
 * - @font-face, @import, @charset, @namespace, @layer, @property are not explicitly handled.
 *   They are passed through unchanged via the `@` prefix check in `scopeSelectorList`.
 *   This works correctly for AI Sections but is implicit behavior.
 */

/**
 * Splits a selector list by commas, respecting parentheses.
 * This handles :is(), :where(), :not(), etc. that may contain commas.
 *
 * @param selector - The CSS selector(s) to split
 * @returns Array of individual selectors
 */
function splitSelectorList(selector: string): string[] {
    const selectors: string[] = [];
    let current = '';
    let parenDepth = 0;

    for (const char of selector) {
        if (char === '(') {
            parenDepth++;
            current += char;
        } else if (char === ')') {
            parenDepth--;
            current += char;
        } else if (char === ',' && parenDepth === 0) {
            selectors.push(current);
            current = '';
        } else {
            current += char;
        }
    }

    if (current) {
        selectors.push(current);
    }

    return selectors;
}

/**
 * Scopes a single selector or comma-separated selector list
 *
 * @param selector - The CSS selector(s) to scope
 * @param scope - The scope selector to add (e.g., '.ai-sections-hero')
 * @param rootSelector - If provided, selectors starting with this will be concatenated
 *                       (same element) instead of made descendants. This handles the case
 *                       where the scope class is on the same element as the root selector.
 */
function scopeSelectorList(selector: string, scope: string, rootSelector?: string): string {
    if (!selector.trim()) {
        return selector;
    }

    // Don't scope @-rules (they're handled separately)
    if (selector.trim().startsWith('@')) {
        return selector;
    }

    // Split by comma for multiple selectors, respecting parentheses (for :is(), :where(), etc.)
    return splitSelectorList(selector)
        .map((s) => {
            const trimmed = s.trim();
            if (!trimmed) return s;

            // Handle :root - replace with scope selector
            if (trimmed === ':root') {
                return scope;
            }

            // Handle selectors starting with :root
            if (trimmed.startsWith(':root')) {
                return scope + trimmed.slice(5);
            }

            // If rootSelector is provided and selector starts with it,
            // concatenate (same element) instead of descendant
            if (rootSelector && trimmed.startsWith(rootSelector)) {
                // .ai-sections-container → .ai-sections-hero.ai-sections-container
                // .ai-sections-container:hover → .ai-sections-hero.ai-sections-container:hover
                return `${scope}${trimmed}`;
            }

            // Prefix with scope (descendant selector)
            return `${scope} ${trimmed}`;
        })
        .join(', ');
}

/**
 * Prefixes all CSS selectors with a scope selector.
 * Handles: regular selectors, pseudo-selectors, media queries, keyframes
 *
 * @param css - The CSS string to scope
 * @param scopeSelector - The selector to prefix (e.g., '.ai-sections-hero')
 * @param rootSelector - Optional. Selectors starting with this will be concatenated
 *                       (same element) instead of made descendants. Use when the scope
 *                       class is on the same element as the root container.
 * @returns The scoped CSS string
 *
 * @example
 * // Without rootSelector - all selectors become descendants
 * scopeCSS('.container { color: red; }', '.scope')
 * // → '.scope .container { color: red; }'
 *
 * @example
 * // With rootSelector - root selectors concatenated, others are descendants
 * scopeCSS('.container { color: red; } .title { color: blue; }', '.scope', '.container')
 * // → '.scope.container { color: red; } .scope .title { color: blue; }'
 */
export function scopeCSS(css: string, scopeSelector: string, rootSelector?: string): string {
    if (!css.trim() || !scopeSelector.trim()) {
        return css;
    }

    // Track if we're inside @keyframes (don't scope keyframe selectors like "from", "to", "50%")
    let result = '';
    let depth = 0;
    let inKeyframes = false;
    let buffer = '';
    let commentBuffer = ''; // Separate buffer for comments to preserve them but exclude from selector
    let inComment = false;

    for (let i = 0; i < css.length; i++) {
        const char = css[i];
        const nextChar = css[i + 1] || '';

        // Handle comments - preserve them but don't include in selector text
        if (char === '/' && nextChar === '*') {
            inComment = true;
            commentBuffer += char;
            continue;
        }
        if (inComment) {
            commentBuffer += char;
            if (char === '*' && nextChar === '/') {
                commentBuffer += nextChar;
                i++;
                inComment = false;
                // Output comment immediately, don't mix with selector
                result += commentBuffer;
                commentBuffer = '';
            }
            continue;
        }

        if (char === '{') {
            // Check if this is an @-rule before the brace
            const trimmedBuffer = buffer.trim();

            if (
                trimmedBuffer.startsWith('@keyframes') ||
                trimmedBuffer.startsWith('@-webkit-keyframes')
            ) {
                // @keyframes - don't scope, just pass through
                inKeyframes = true;
                result += buffer + char;
                buffer = '';
                depth++;
            } else if (
                trimmedBuffer.startsWith('@media') ||
                trimmedBuffer.startsWith('@supports')
            ) {
                // @media/@supports - pass through the @-rule, scope contents
                result += buffer + char;
                buffer = '';
                depth++;
            } else if (depth === 0 || (depth === 1 && !inKeyframes)) {
                // Regular selector at root level or inside @media/@supports
                // Scope the selector
                const scopedSelector = scopeSelectorList(
                    trimmedBuffer,
                    scopeSelector,
                    rootSelector
                );
                result += scopedSelector + ' ' + char;
                buffer = '';
                depth++;
            } else {
                // Inside keyframes or nested - don't scope
                result += buffer + char;
                buffer = '';
                depth++;
            }
        } else if (char === '}') {
            result += buffer + char;
            buffer = '';
            depth--;
            if (depth === 0) {
                inKeyframes = false;
            }
        } else {
            buffer += char;
        }
    }

    // Append any remaining buffer
    result += buffer;

    return result;
}
